import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  FormItem,
  Input,
  ListGroup,
  Modal,
  Overlay,
  Text,
} from '@cubeyou-team/hx-react-components';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';

import translations from 'src/lang';
import { SelectedDestinationItem } from 'src/modules/audience-activation/types';
import { RequiredText } from '../../RequiredText';
import { DestinationImage } from '../DestinationImage';

const lang = 'en-US';

const StyledDestinationDetailModal = styled(Modal)`
  > div {
    padding-bottom: 0;
  }
`;

const StyledImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 0.25rem;
  margin-bottom: 1.25rem;

  .image {
    width: 2.75rem;
    height: 2.75rem;
  }
`;

export const MODAL_STATUS = {
  NEW: 'NEW',
  EDIT: 'EDIT',
} as const;

export interface ModalDetail {
  isOpen: boolean;
  status: keyof typeof MODAL_STATUS;
}
interface DestinationDetailModalProps {
  modalDetail: ModalDetail;
  destinationDetails: SelectedDestinationItem;
  onClose: () => void;
  onSuccess: (
    additionalPropPayload: Array<{ key: string; value: string }>
  ) => void;
}

export const DestinationDetailModal: FC<DestinationDetailModalProps> = ({
  modalDetail: detailModal,
  destinationDetails,
  onClose,
  onSuccess,
}) => {
  const additionalProps = useMemo(
    () => destinationDetails?.additional_properties || [],
    [destinationDetails]
  );

  const schema = useMemo(() => {
    const shape: Record<string, any> = {};

    additionalProps?.forEach(prop => {
      shape[prop.key] = yup
        .string()
        .required(translations[lang].COMMON.ERROR_MESSAGES.MANDATORY_FIELD);
    });

    return yup.object().shape(shape);
  }, [additionalProps]);

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
    getValues,
  } = useForm<any>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: Object.fromEntries(
      additionalProps.map(prop => [prop.key, ''])
    ),
  });

  const watchedValues = useWatch<any>({ control });

  useEffect(() => {
    if (additionalProps) {
      reset(
        Object.fromEntries(
          additionalProps.map(({ key, value }) => [key, value])
        )
      );
    }
  }, [reset, additionalProps]);

  const onSubmit = () => {
    const values = getValues();
    const keyValueArray = additionalProps.map(prop => ({
      key: prop.key,
      value: values[prop.key],
    }));
    onSuccess(keyValueArray);
  };

  return (
    <Overlay isOpen={detailModal.isOpen}>
      <StyledDestinationDetailModal
        title={translations[lang].MODALS.DESTINATION_DETAILS.MODAL_TITLE}
        footer={
          <ListGroup
            direction="row"
            items={[
              <Button
                type="submit"
                size="small"
                onClick={handleSubmit(onSubmit)}
                disabled={additionalProps.some(
                  prop => !watchedValues?.[prop.key]?.length
                )}
              >
                {translations[lang].MODALS.DESTINATION_DETAILS.BUTTON_ADD}
              </Button>,
              <Button size="small" variant={'outlined'} onClick={onClose}>
                {translations[lang].MODALS.DESTINATION_DETAILS.BUTTON_CANCEL}
              </Button>,
            ]}
          />
        }
        closable
        handleClose={onClose}
      >
        <StyledImageContainer>
          <DestinationImage
            src={destinationDetails?.image}
            alt={destinationDetails?.destination}
          />
          <Text size="xsmall" align="center" className="title">
            {destinationDetails?.destination}
          </Text>
        </StyledImageContainer>

        {additionalProps.map(prop => (
          <Controller
            key={prop.key}
            {...({
              name: prop.key,
              control,
              render: ({ field }) => (
                <FormItem
                  error={!!errors?.[prop.key]}
                  message={errors?.[prop.key]?.message}
                  label={
                    translations[lang].MODALS.DESTINATION_DETAILS
                      .DESTINATION_PROPERTY_INPUT_LABEL
                  }
                  buttonIconTooltipMessage={
                    translations[lang].MODALS.DESTINATION_DETAILS
                      .INFO_ICON_TOOLTIP_MESSAGE
                  }
                  buttonIconTooltipPosition="left"
                  icon="informations"
                >
                  <Input
                    {...field}
                    placeholder={prop.key}
                    type="text"
                    size="small"
                    tabIndex={1}
                    required
                    error={!!errors?.[prop.key]}
                  />
                </FormItem>
              ),
            } as any)} // casting the entire props to any
          />
        ))}

        <RequiredText />
      </StyledDestinationDetailModal>
    </Overlay>
  );
};
